import React from 'react';

// Shared chat helpers + safe Markdown renderer, used by both the Ask AI and
// Carrier Appetite Guide drawers. No dangerouslySetInnerHTML — React escapes
// all text, so there is no XSS risk.

export function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `conv-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function truncate(text: string, max = 60): string {
  return text.length > max ? text.slice(0, max) + '…' : text;
}

function renderInline(text: string, key: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let i = 0;

  while (remaining.length > 0) {
    const boldMatch   = remaining.match(/\*\*\*(.+?)\*\*\*/);
    const strongMatch = remaining.match(/\*\*(.+?)\*\*/);
    const emMatch     = remaining.match(/\*(.+?)\*/);
    const codeMatch   = remaining.match(/`(.+?)`/);

    const candidates = [boldMatch, strongMatch, emMatch, codeMatch]
      .filter(Boolean)
      .sort((a, b) => (a!.index ?? 0) - (b!.index ?? 0));

    if (candidates.length === 0) {
      parts.push(remaining);
      break;
    }

    const first = candidates[0]!;
    if ((first.index ?? 0) > 0) {
      parts.push(remaining.slice(0, first.index));
    }

    const inner = first[1];
    const k = `${key}-${i++}`;
    if (first === boldMatch)        parts.push(<strong key={k}><em>{inner}</em></strong>);
    else if (first === strongMatch) parts.push(<strong key={k}>{inner}</strong>);
    else if (first === emMatch)     parts.push(<em key={k}>{inner}</em>);
    else                            parts.push(<code key={k}>{inner}</code>);

    remaining = remaining.slice((first.index ?? 0) + first[0].length);
  }
  return parts;
}

function normalizeTableBlocks(text: string): string {
  const lines = text.split('\n');
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const prev = out[out.length - 1] ?? '';
    if (line.trimStart().startsWith('|') && !prev.trimStart().startsWith('|') && prev.trim() !== '') {
      out.push('');
    }
    out.push(line);
  }
  return out.join('\n');
}

export function MarkdownContent({ text }: { text: string }): React.ReactElement {
  const blocks = normalizeTableBlocks(text).split(/\n{2,}/);
  const elements: React.ReactNode[] = [];

  blocks.forEach((block, bi) => {
    const trimmed = block.trim();
    if (!trimmed) return;

    if (/^---+$/.test(trimmed)) {
      elements.push(<hr key={bi} />);
      return;
    }

    const h3 = trimmed.match(/^### (.+)$/);
    const h2 = trimmed.match(/^#{1,2} (.+)$/);
    if (h3) { elements.push(<h3 key={bi}>{renderInline(h3[1], `h3-${bi}`)}</h3>); return; }
    if (h2) { elements.push(<h2 key={bi}>{renderInline(h2[1], `h2-${bi}`)}</h2>); return; }

    if (/^\|.+\|/.test(trimmed)) {
      const rows = trimmed.split('\n').filter((r) => !/^\|[-| :]+\|$/.test(r));
      if (rows.length >= 2) {
        const headers = rows[0].split('|').slice(1, -1).map((c) => c.trim());
        elements.push(
          <div className="qd-chat-tablewrap" key={bi}>
            <table>
              <thead>
                <tr>{headers.map((h, hi) => <th key={hi}>{renderInline(h, `th-${bi}-${hi}`)}</th>)}</tr>
              </thead>
              <tbody>
                {rows.slice(1).map((row, ri) => {
                  const cells = row.split('|').slice(1, -1).map((c) => c.trim());
                  return (
                    <tr key={ri}>
                      {cells.map((c, ci) => <td key={ci}>{renderInline(c, `td-${bi}-${ri}-${ci}`)}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
        return;
      }
    }

    const listLines = trimmed.split('\n').filter((l) => /^[*-] /.test(l));
    if (listLines.length > 0 && listLines.length === trimmed.split('\n').length) {
      elements.push(
        <ul key={bi}>
          {listLines.map((l, li) => (
            <li key={li}>{renderInline(l.replace(/^[*-] /, ''), `li-${bi}-${li}`)}</li>
          ))}
        </ul>
      );
      return;
    }

    const lines = trimmed.split('\n');
    elements.push(
      <p key={bi}>
        {lines.map((line, li) => (
          <React.Fragment key={li}>
            {renderInline(line, `p-${bi}-${li}`)}
            {li < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
  });

  return <>{elements}</>;
}
